import { ClockState, PunchType, enumKeyOf } from '@mai-team-amusement/core';
import { useCommonT, useLocaleState } from '@mai-team-amusement/ui';
import type { ClockStatus } from '@mai-work-clock/contracts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { ClockQueryKey, clockApi } from '../api.js';
import { ClockStateChip, ErrorAlert, QueryView, Section, Space } from '../components/common.js';
import { formatTime } from '../format.js';
import {
  GeolocationFailure,
  LocationPermission,
  currentPosition,
  useLocationPermission,
  type GeoFailure,
} from '../geolocation.js';
import { useClockT } from '../i18n/index.js';

const TICK_MS = 1000;
const CLOCK_FONT_SIZE = { xs: '3rem', sm: '4.5rem' };
const BUTTON_MIN_HEIGHT = 64;
const BUTTON_MIN_WIDTH = 200;

/** The current time on the server's clock (corrected for this device's clock offset). */
function useServerNow(status: ClockStatus | undefined, fetchedAt: number): Date {
  const offset = status === undefined ? 0 : Date.parse(status.now) - fetchedAt;
  const [now, setNow] = useState(() => new Date(Date.now() + offset));
  useEffect(() => {
    setNow(new Date(Date.now() + offset));
    const timer = setInterval(() => {
      setNow(new Date(Date.now() + offset));
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [offset]);
  return now;
}

const BUTTON_COLORS: Record<PunchType, 'primary' | 'secondary' | 'success' | 'warning'> = {
  [PunchType.StartWork]: 'success',
  [PunchType.StartBreak]: 'warning',
  [PunchType.EndBreak]: 'primary',
  [PunchType.EndWork]: 'secondary',
};

function LocationStatus({ locating }: { locating: boolean }) {
  const t = useClockT();
  const permission = useLocationPermission();
  if (locating) {
    return <Alert severity="info">{t('clock.location.locating')}</Alert>;
  }
  switch (permission) {
    case LocationPermission.Granted:
      return (
        <Alert severity="success" icon={<LocationOnIcon />}>
          {t('clock.location.granted')}
        </Alert>
      );
    case LocationPermission.Denied:
      return (
        <Alert severity="warning" icon={<LocationOffIcon />}>
          {t('clock.location.denied')}
        </Alert>
      );
    case LocationPermission.Unsupported:
      return (
        <Alert severity="warning" icon={<LocationOffIcon />}>
          {t('clock.location.unsupported')}
        </Alert>
      );
    case LocationPermission.Prompt:
    case LocationPermission.Unknown:
      return (
        <Alert severity="info" icon={<LocationOnIcon />}>
          {t('clock.location.prompt')}
        </Alert>
      );
  }
}

export function ClockPage() {
  const t = useClockT();
  const common = useCommonT();
  const { locale } = useLocaleState();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ClockQueryKey.Status, queryFn: clockApi.status });
  const now = useServerNow(status.data, status.dataUpdatedAt);
  const [locating, setLocating] = useState(false);
  const [failure, setFailure] = useState<GeoFailure | null>(null);
  const [recordedAt, setRecordedAt] = useState<string | null>(null);

  const punch = useMutation({
    mutationFn: async (type: PunchType) => {
      setRecordedAt(null);
      setLocating(true);
      const position = await currentPosition().finally(() => {
        setLocating(false);
      });
      return clockApi.punch({ type, position });
    },
    onSuccess: (next) => {
      queryClient.setQueryData(ClockQueryKey.Status, next);
      void queryClient.invalidateQueries({ queryKey: ClockQueryKey.Records });
      setRecordedAt(next.punches[next.punches.length - 1]?.occurredAt ?? null);
    },
    onError: (error) => {
      if (error instanceof GeolocationFailure) {
        setFailure(error.failure);
      }
    },
  });
  const apiError = punch.error instanceof GeolocationFailure ? null : punch.error;

  return (
    <QueryView query={status}>
      {(data) => {
        const local = DateTime.fromJSDate(now, { zone: data.timeZone }).setLocale(locale);
        const corrections = data.needsCorrection;
        return (
          <Stack spacing={Space.Md}>
            {corrections.length === 0 ? null : (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    component={RouterLink}
                    to={`/requests/new?date=${corrections[0] ?? ''}`}
                  >
                    {t('clock.requestCorrection')}
                  </Button>
                }
              >
                {t('clock.needsCorrection', { dates: corrections.join(', ') })}
              </Alert>
            )}
            <Section>
              <Stack spacing={Space.Sm} sx={{ alignItems: 'center', textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('clock.now', { timeZone: data.timeZone })}
                </Typography>
                <Typography
                  component="p"
                  sx={{ fontSize: CLOCK_FONT_SIZE, fontVariantNumeric: 'tabular-nums' }}
                  aria-live="off"
                >
                  {local.toFormat('HH:mm:ss')}
                </Typography>
                <Typography>
                  {local.toLocaleString({
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    weekday: 'long',
                  })}
                </Typography>
                <Stack direction="row" spacing={Space.Xs} sx={{ alignItems: 'center' }}>
                  <ClockStateChip state={data.state} />
                  {data.state !== ClockState.OffDuty && data.workDay !== null ? (
                    <Typography color="text.secondary">
                      {t('clock.since', { time: formatTime(data.workDay.startAt, data.timeZone) })}
                    </Typography>
                  ) : null}
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={Space.Sm}
                  sx={{ width: '100%', justifyContent: 'center' }}
                >
                  {data.allowedPunches.map((type) => (
                    <Button
                      key={type}
                      variant="contained"
                      size="large"
                      color={BUTTON_COLORS[type]}
                      loading={punch.isPending && punch.variables === type}
                      disabled={punch.isPending}
                      sx={{
                        minHeight: BUTTON_MIN_HEIGHT,
                        minWidth: { sm: BUTTON_MIN_WIDTH },
                        fontSize: '1.2rem',
                      }}
                      onClick={() => {
                        punch.mutate(type);
                      }}
                    >
                      {common(`enum.PunchType.${enumKeyOf(PunchType, type)}`)}
                    </Button>
                  ))}
                </Stack>
                {data.state === ClockState.OffDuty &&
                data.allowedPunches.includes(PunchType.EndWork) ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('clock.endAgainHint')}
                  </Typography>
                ) : null}
                {recordedAt === null ? null : (
                  <Alert severity="success" icon={<CheckCircleIcon />} role="status">
                    {t('clock.recorded', { time: formatTime(recordedAt, data.timeZone) })}
                  </Alert>
                )}
                <ErrorAlert error={apiError} />
              </Stack>
            </Section>
            <LocationStatus locating={locating} />
            <Section title={t('clock.today')}>
              {data.punches.length === 0 ? (
                <Typography color="text.secondary">{t('clock.noPunches')}</Typography>
              ) : (
                <List dense disablePadding>
                  {data.punches.map((entry) => (
                    <ListItem key={entry.id} disableGutters>
                      <ListItemText
                        primary={common(`enum.PunchType.${enumKeyOf(PunchType, entry.type)}`)}
                        secondary={formatTime(entry.occurredAt, data.timeZone)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Section>
            <Dialog
              open={failure !== null}
              onClose={() => {
                setFailure(null);
              }}
            >
              <DialogTitle>{t('clock.error.title')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {failure === null ? null : t(`clock.error.${failure}`)}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setFailure(null);
                  }}
                >
                  {common('action.close')}
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        );
      }}
    </QueryView>
  );
}
